import { useState } from 'react';

import {
  type BibleFigures,
  type Content,
  contentPath,
  describeProductionSlots,
  ideaBatchPath,
  type Repository,
  retryContent,
  type Stats,
  type StorageSettings,
} from '../api.ts';
import {
  attentionReason,
  describeError,
  isActive,
  isEnded,
  needsAttention,
  useAppData,
  useKeyChords,
} from '../data.tsx';
import { DeliveredStrip } from '../deliveries.tsx';
import { Icon } from '../Icon.tsx';
import { Link, navigate } from '../router.tsx';
import { localTime, since, startsIn } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import {
  Badge,
  Bars,
  bytesLabel,
  EmptyState,
  Figure,
  gigabytes,
  Loader,
  Meter,
  PageHeader,
  Panel,
  Progress,
  Skeleton,
  StatusBadge,
  StatusDot,
} from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * The home page: whether the install is set up, what needs the operator, and
 * what the machine is doing right now.
 *
 * Every number on it comes from the app-wide poll in `data.tsx` — the content
 * list and `GET /api/stats` — so the page is never more than a few seconds
 * behind the production loop and never starts a poll of its own.
 */
export function Overview() {
  const { contents, repositories, stats, claude, storage, error, refresh } = useAppData();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  useKeyChords({ n: () => navigate('/content/new') });

  /**
   * The one **Retry** (US-034): the server decides from the stage whether that
   * is the clone again or the production resumed. A retry it cannot run is a
   * `200` with the sentence that says why, the same as a clone that failed.
   */
  const onRetry = (content: Content): void => {
    setBusyId(content.id);
    retryContent(content.id)
      .then(async (outcome) => {
        await refresh();
        if (outcome.ok) toast.ok(outcome.plan.summary);
        else toast.error(outcome.message ?? `${content.name} cannot be retried.`);
      })
      .catch((cause: unknown) => {
        toast.error(`Retry: ${describeError(cause)}`);
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  const all = contents ?? [];
  const attention = all.filter(needsAttention);
  // A failure is `danger`; a content that only waits at a gate is `wait`.
  const attentionTone = attention.some((content) => content.status === 'failed') ? 'danger' : 'wait';
  const running = [...all].filter(isActive).sort((a, b) => {
    const rank = (content: Content): number =>
      content.status === 'producing' ? 0 : content.status === 'waiting' ? 1 : 2;
    return rank(a) - rank(b) || (a.queuePosition ?? 0) - (b.queuePosition ?? 0);
  });
  const upcoming = all
    .filter(
      (content) =>
        content.scheduledStartAt !== null &&
        !content.scheduleMissed &&
        (content.status === 'pending' || content.status === 'ready'),
    )
    .sort((a, b) => (a.scheduledStartAt ?? '').localeCompare(b.scheduledStartAt ?? ''));
  // Everything whose production is over, including the delivered ones: the
  // question this answers is "what came out lately", not "what is unapproved".
  const finished = all
    .filter(isEnded)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 6);

  const setup = {
    claude: claude?.status.authenticated === true,
    repository: (repositories ?? []).some((repository) => repository.keyConfigured),
    content: all.length > 0,
    storage: storageStep(storage),
  };
  // Held back until every input has landed, so a set-up install never shows
  // the checklist for the one poll before its own data arrives.
  const showSetup =
    claude !== null &&
    repositories !== null &&
    contents !== null &&
    storage !== null &&
    !(setup.claude && setup.repository && setup.content && setup.storage.done);

  return (
    <div className="page">
      <PageHeader
        title="Overview"
        subtitle={
          stats === null
            ? 'Reading the server…'
            : `${String(stats.productions.active)} of ${String(stats.productions.max)} production slots in use` +
              (stats.productions.queued > 0 ? ` · ${String(stats.productions.queued)} queued` : '') +
              ` · ${String(all.length)} ${all.length === 1 ? 'content' : 'contents'}`
        }
        actions={
          <Link className="button button--primary" href="/content/new" title="n">
            <Icon name="plus" />
            New content
          </Link>
        }
      />

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">Could not read contents: {error}</div>
        </div>
      )}

      {showSetup && <SetupChecklist {...setup} />}

      {attention.length > 0 && (
        <Panel
          title="Needs you"
          icon="alert"
          tone={attentionTone === 'danger' ? 'danger' : 'warn'}
          meta={<Badge tone={attentionTone}>{attention.length}</Badge>}
          actions={
            <Link className="link" href="/content?filter=attention">
              All of them
            </Link>
          }
        >
          <ul className="rows">
            {attention.map((content) => (
              <li className="row" key={content.id}>
                <StatusDot status={content.status} />
                <div className="row__main">
                  <Link className="row__title" href={contentPath(content.id)}>
                    {content.name}
                  </Link>
                  <span className="row__meta">
                    {content.kind} · {attentionReason(content)}
                  </span>
                  {busyId === content.id && (
                    <Loader label="Retrying: setting up again or resuming production…" size="sm" />
                  )}
                </div>
                <div className="row__actions">
                  {content.status === 'failed' && (
                    <button
                      type="button"
                      className="button button--small"
                      onClick={() => {
                        onRetry(content);
                      }}
                      disabled={busyId !== null}
                      // The plan itself is on the content page: from a list
                      // row, what the operator is told is the guarantee every
                      // stage shares.
                      title="Retry: resumes on the same workspace, at the stage this content failed at"
                    >
                      <Icon name="sync" />
                      {busyId === content.id ? 'Working…' : 'Retry'}
                    </button>
                  )}
                  <Link
                    className="button button--small button--quiet"
                    href={contentPath(content.id)}
                  >
                    Open
                    <Icon name="chevron-right" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {stats !== null && <IdeasPanel ideas={stats.ideas} />}

      <div className="grid grid--main-aside">
        <Panel
          title="Right now"
          icon="pulse"
          meta={
            stats !== null && (
              <span className="panel__meta" title={describeProductionSlots(stats.productions)}>
                <Meter
                  value={stats.productions.active}
                  max={stats.productions.max}
                  label={describeProductionSlots(stats.productions)}
                />
                <span className="mono">
                  {stats.productions.active}/{stats.productions.max}
                </span>
              </span>
            )
          }
          actions={
            <Link className="link" href="/content?filter=active">
              All active
            </Link>
          }
        >
          {contents === null ? (
            <Skeleton lines={3} />
          ) : running.length === 0 ? (
            <EmptyState icon="zap" title="Nothing is in production">
              <p>
                {all.some((content) => content.status === 'ready')
                  ? 'A ready content is waiting for you to start it.'
                  : 'Plan a content, mark it ready, and start it; it appears here while it runs.'}
              </p>
              <p className="muted">
                <Icon name="list" /> US-030 starts the production loop, so this stays empty until
                Phase 3.
              </p>
            </EmptyState>
          ) : (
            <ul className="rows">
              {running.map((content) => (
                <li className="row" key={content.id}>
                  <StatusDot status={content.status} />
                  <div className="row__main">
                    <Link className="row__title" href={contentPath(content.id)}>
                      {content.name}
                    </Link>
                    <span className="row__meta">
                      {content.kind}
                      {content.queuePosition !== null &&
                        ` · queued #${String(content.queuePosition)}`}
                      {content.status === 'waiting' &&
                        content.waitingUntil !== null &&
                        ` · resumes ${startsIn(content.waitingUntil).replace('starts ', '')}`}
                    </span>
                  </div>
                  <div className="row__progress">
                    <Progress
                      done={content.steps.done}
                      total={content.steps.total}
                      tone={content.status === 'waiting' ? 'wait' : 'active'}
                      compact
                    />
                  </div>
                  <StatusBadge status={content.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <div className="stack">
          <Panel title="Up next" icon="calendar">
            {contents === null ? (
              <Skeleton lines={2} />
            ) : upcoming.length === 0 ? (
              <p className="muted">
                No scheduled starts. Set one on a content to produce it while you are away.
              </p>
            ) : (
              <ul className="rows rows--tight">
                {upcoming.slice(0, 5).map((content) => (
                  <li className="row" key={content.id}>
                    <div className="row__main">
                      <Link className="row__title" href={contentPath(content.id)}>
                        {content.name}
                      </Link>
                      <span className="row__meta">
                        {localTime(content.scheduledStartAt ?? '')} ·{' '}
                        {startsIn(content.scheduledStartAt ?? '')}
                      </span>
                    </div>
                    <StatusBadge status={content.status} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Machine stats={stats} />

          <BucketPanel storage={storage} />
        </div>
      </div>

      <Activity stats={stats} />

      {/* What came out, as pictures rather than as rows (US-074). Below the
          work in flight and above the reference lists: the operator's first
          question is what needs them, the second is what they have made. */}
      <DeliveredStrip contents={contents} />

      <div className="grid grid--halves">
        <Panel
          title="Repositories"
          icon="repo"
          actions={
            <Link className="link" href="/repositories">
              Manage
            </Link>
          }
        >
          {repositories === null ? (
            <Skeleton lines={3} />
          ) : repositories.length === 0 ? (
            <EmptyState
              icon="repo"
              title="No repositories yet"
              action={
                <Link className="button" href="/repositories">
                  Add a repository
                </Link>
              }
            >
              <p>Every content is made from a repository it is allowed to read.</p>
            </EmptyState>
          ) : (
            <ul className="rows rows--tight">
              {repositories.map((repository) => (
                <li className="row" key={repository.id}>
                  <Icon name="repo" className="text-muted" />
                  <div className="row__main">
                    <Link
                      className="row__title"
                      href={`/content?repository=${encodeURIComponent(repository.id)}`}
                    >
                      {repository.name}
                    </Link>
                    <span className="row__meta">{describeRepository(repository, all)}</span>
                  </div>
                  {!repository.keyConfigured && <Badge tone="danger">no key</Badge>}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Recently finished"
          icon="check-circle"
          actions={
            <Link className="link" href="/content?filter=delivered">
              All delivered
            </Link>
          }
        >
          {contents === null ? (
            <Skeleton lines={3} />
          ) : finished.length === 0 ? (
            <p className="muted">
              Nothing has finished yet. A content reaches this list once a version has been
              rendered and it is waiting for — or has had — your approval.
            </p>
          ) : (
            <ul className="rows rows--tight">
              {finished.map((content) => (
                <li className="row" key={content.id}>
                  <StatusDot status={content.status} />
                  <div className="row__main">
                    <Link className="row__title" href={contentPath(content.id)}>
                      {content.name}
                    </Link>
                    <span className="row__meta">
                      {content.kind} · {content.versionCount}{' '}
                      {content.versionCount === 1 ? 'version' : 'versions'} ·{' '}
                      {since(content.updatedAt)}
                    </span>
                  </div>
                  <StatusBadge status={content.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** What this repository is to the contents that exist: how many, how many live. */
/**
 * Ideas waiting on the operator, and batches still being worked (US-125), so
 * a finished batch does not sit unnoticed. Absent while there is neither.
 */
function IdeasPanel({ ideas }: { readonly ideas: Stats['ideas'] }) {
  const { toDecide, working, newestBatchToDecide } = ideas;
  if (toDecide === 0 && working === 0) return null;
  return (
    <Panel
      title={wording.IDEAS}
      icon="bulb"
      actions={
        <Link className="link" href="/ideas">
          {wording.ALL_BATCHES}
        </Link>
      }
    >
      <ul className="rows rows--tight">
        {toDecide > 0 && (
          <li className="row">
            <div className="row__main">
              {newestBatchToDecide === null ? (
                <span className="row__title">{wording.ideasWaiting(toDecide)}</span>
              ) : (
                <Link className="row__title" href={ideaBatchPath(newestBatchToDecide)}>
                  {wording.ideasWaiting(toDecide)}
                </Link>
              )}
            </div>
            <Badge tone="wait">{toDecide}</Badge>
          </li>
        )}
        {working > 0 && (
          <li className="row">
            <div className="row__main">
              <Loader label={wording.IDEAS_WORKING} size="sm" />
            </div>
          </li>
        )}
      </ul>
    </Panel>
  );
}

function describeRepository(repository: Repository, contents: readonly Content[]): string {
  const used = contents.filter((content) =>
    content.repositories.some((link) => link.repositoryId === repository.id),
  );
  if (used.length === 0) return 'no content yet';
  const active = used.filter(isActive).length;
  const parts = [`${String(used.length)} ${used.length === 1 ? 'content' : 'contents'}`];
  if (active > 0) parts.push(`${String(active)} in production`);
  return parts.join(' · ');
}

/**
 * The machine marketingchief runs on. The sidebar carries the same two numbers
 * as gauges at all times; here they are spelled out next to the slots they
 * explain, because "nothing is starting" and "the host is out of memory" are
 * the same answer often enough.
 */
function Machine({ stats }: { readonly stats: Stats | null }) {
  if (stats === null) {
    return (
      <Panel title="Machine" icon="graph">
        <Skeleton lines={2} />
      </Panel>
    );
  }
  const { host, productions } = stats;
  const facts = [
    {
      label: `CPU · ${String(host.cores)} ${host.cores === 1 ? 'core' : 'cores'}`,
      value: host.cpu === null ? '…' : `${String(Math.round(host.cpu * 100))}%`,
    },
    {
      label: 'Memory',
      value: `${gigabytes(host.memory.used)}/${gigabytes(host.memory.total)} GB`,
    },
    { label: 'Free slots', value: String(Math.max(0, productions.free)) },
    { label: 'Steps done', value: `${String(stats.steps.done)}/${String(stats.steps.total)}` },
  ];
  return (
    <Panel title="Machine" icon="graph">
      <div className="host-facts">
        {facts.map((fact) => (
          <div className="host-fact" key={fact.label}>
            <span className="host-fact__value">{fact.value}</span>
            <span className="host-fact__label">{fact.label}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/**
 * What is in the bucket that this server is not tracking (US-057).
 *
 * The nightly scan counts objects under the key prefix with no asset row —
 * what a content deleted with **keep copies on delete** left behind, an upload
 * interrupted before its row, a bucket shared with another install. The panel
 * says the count, the size and when it looked, and it says in as many words
 * that nothing is removed for you: this is the one number on the overview an
 * operator might otherwise read as an offer to tidy up.
 *
 * It is absent entirely until a bucket is configured — the set-up checklist is
 * what talks about storage before that.
 */
function BucketPanel({ storage }: { readonly storage: StorageSettings | null }) {
  if (storage === null || !storage.configured) return null;
  const report = storage.orphans;
  return (
    <Panel
      title="Bucket"
      icon="package"
      actions={
        <Link className="link" href="/settings#storage">
          Storage
        </Link>
      }
    >
      {report === null ? (
        <p className="muted">
          {storage.bucket} has not been scanned yet. Every night marketingchief counts the objects
          under <code>{storage.keyPrefix === '' ? '/' : storage.keyPrefix}</code> that no asset row
          claims.
        </p>
      ) : (
        <>
          <div className="host-facts">
            <div className="host-fact">
              <span className="host-fact__value">{report.orphans}</span>
              <span className="host-fact__label">untracked objects</span>
            </div>
            <div className="host-fact">
              <span className="host-fact__value">{bytesLabel(report.bytes)}</span>
              <span className="host-fact__label">they occupy</span>
            </div>
            <div className="host-fact">
              <span className="host-fact__value">{report.scanned}</span>
              <span className="host-fact__label">objects scanned</span>
            </div>
          </div>
          {report.error !== null ? (
            <p className="muted">
              The scan {since(report.ranAt)} could not finish: {report.error}
            </p>
          ) : report.orphans === 0 ? (
            <p className="muted">
              Every object in {storage.bucket} belongs to a content this server knows about.
              Scanned {since(report.ranAt)}.
            </p>
          ) : (
            <>
              <p className="muted">
                Scanned {since(report.ranAt)}. <strong>Nothing is ever deleted for you</strong> —
                these are objects marketingchief no longer tracks, usually contents deleted while
                copies were kept. Remove them in {storage.bucket} if you want the space back.
              </p>
              {report.sample.length > 0 && (
                <ul className="rows rows--tight">
                  {report.sample.map((key) => (
                    <li className="row" key={key}>
                      <span className="row__meta mono">{key}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * The last two weeks, as numbers and bars, plus what it has cost (US-079).
 *
 * Every figure comes off the one `GET /api/stats` the app shell already polls,
 * so the panel costs no request of its own, and the window is the server's —
 * fourteen calendar days in UTC, ending today.
 *
 * The three the operator answers "is this worth running?" with are the first
 * three: how often something is delivered, what a day costs, and what one
 * finished asset costs. The last is not the second divided by the first —
 * see `Throughput` in `api.ts` — and the note under the row says so,
 * because two of these numbers sitting next to each other is exactly the
 * invitation to multiply them out.
 */
function Activity({ stats }: { readonly stats: Stats | null }) {
  if (stats === null) {
    return (
      <div className="figures">
        <Skeleton lines={2} />
      </div>
    );
  }
  const days = stats.activity;
  const labels = days.map((day) => day.day);
  const sum = (pick: (day: Stats['activity'][number]) => number): number =>
    days.reduce((total, day) => total + pick(day), 0);
  const { throughput } = stats;
  const delivered = stats.contents.byStatus.delivered;
  const outcomes = delivered + stats.contents.byStatus.failed;
  const successRate = outcomes === 0 ? null : Math.round((delivered / outcomes) * 100);
  const providers = stats.spend.byProvider.length;

  // "No data yet" is about the install, not about the window: a server that
  // has never had a content, never run a step and never been billed has
  // nothing to average, and fourteen zeroes in a row say that far less
  // clearly than a sentence does. An install that simply had a quiet
  // fortnight keeps its figures — those zeroes are the answer.
  if (stats.contents.total === 0 && stats.steps.total === 0 && stats.spend.totalUsd === 0) {
    return (
      <Panel title="Throughput" icon="graph">
        <EmptyState icon="graph" title="Nothing has been produced yet">
          <p>
            Once a content has been through production, this is where{' '}
            <strong>how much gets delivered per day</strong>, <strong>what a day costs</strong> and{' '}
            <strong>what one finished asset costs</strong> over the last{' '}
            {String(throughput.days)} days appear.
          </p>
        </EmptyState>
        <p className="muted">
          Nothing is billed until a step calls a paid provider, so a brief you never produce costs
          nothing to keep.
        </p>
      </Panel>
    );
  }

  const quiet = throughput.delivered === 0 && throughput.spendUsd === 0;

  return (
    <>
      <div className="figures">
        <Figure
          label={`contents delivered per day, ${String(throughput.days)} days`}
          value={throughput.deliveredPerDay.toFixed(2)}
          hint={
            <Bars
              values={days.map((day) => day.contentsDelivered)}
              labels={labels}
              ariaLabel="Contents delivered per day"
            />
          }
          tone="final"
        />
        <Figure
          label={`spend per day, ${String(throughput.days)} days`}
          value={usd(throughput.spendPerDayUsd)}
          hint={
            <Bars
              values={days.map((day) => day.spendUsd)}
              labels={labels}
              ariaLabel="Spend per day, in dollars"
            />
          }
        />
        <Figure
          label="average cost per delivered content"
          value={
            throughput.costPerDeliveredUsd === null ? '—' : usd(throughput.costPerDeliveredUsd)
          }
          hint={
            <span className="figure__note">
              {throughput.costPerDeliveredUsd === null
                ? `nothing delivered in ${String(throughput.days)} days`
                : `${String(throughput.delivered)} delivered · every call they made`}
            </span>
          }
          tone="done"
        />
        <Figure
          label={`steps done, ${String(throughput.days)} days`}
          value={sum((day) => day.stepsDone)}
          hint={
            <Bars
              values={days.map((day) => day.stepsDone)}
              labels={labels}
              ariaLabel="Steps done per day"
            />
          }
          tone="active"
        />
        <Figure
          label={`contents started, ${String(throughput.days)} days`}
          value={sum((day) => day.contentsCreated)}
          hint={
            <Bars
              values={days.map((day) => day.contentsCreated)}
              labels={labels}
              ariaLabel="Contents created per day"
            />
          }
        />
        <Figure
          label="spend, month to date"
          value={`$${stats.spend.monthToDateUsd.toFixed(2)}`}
          hint={
            <span className="figure__note">
              {successRate === null
                ? 'nothing delivered or failed yet'
                : `${String(successRate)}% delivered of ${String(outcomes)} finished`}
              {providers > 0 &&
                ` · ${String(providers)} ${providers === 1 ? 'provider' : 'providers'}`}
            </span>
          }
          tone={successRate !== null && successRate < 50 ? 'danger' : undefined}
        />
      </div>
      <OneShotFigures stats={stats} />
      <p className="muted">
        {quiet
          ? `Nothing was delivered and nothing was billed between ${throughput.since} and today.`
          : `${String(throughput.delivered)} delivered and ${usd(throughput.spendUsd)} billed since ${throughput.since}.`}{' '}
        The average per delivered content counts <em>everything those contents ever cost</em>, not
        only what was billed inside the window — so it does not divide out of the two figures
        beside it. A failed or refused provider call was never billed and is never spend.
      </p>
    </>
  );
}

/**
 * Does version 1 ship? (one-shot ads US-037) Four figures over the contents
 * delivered in the window. The per-content figures are averages over the same
 * contents and do not multiply out of each other.
 */
function OneShotFigures({ stats }: { readonly stats: Stats }) {
  const { oneShot, throughput, bible } = stats;
  const none = `nothing delivered in ${String(throughput.days)} days`;
  const perDelivered = (value: number | null): string => (value === null ? '—' : value.toFixed(2));
  return (
    <div className="figures">
      <Figure
        label="version 1 shipped"
        value={oneShot.rate === null ? '—' : `${String(Math.round(oneShot.rate * 100))}%`}
        hint={
          <span className="figure__note">
            {oneShot.rate === null
              ? none
              : `${String(oneShot.oneShot)} of ${String(oneShot.delivered)} delivered, no change asked`}
          </span>
        }
        tone={oneShot.rate === null ? undefined : 'final'}
      />
      <Figure
        label="your revisions per delivered content"
        value={perDelivered(oneShot.revisionsPerDelivered)}
        hint={
          <span className="figure__note">
            {oneShot.revisionsPerDelivered === null
              ? none
              : `${String(oneShot.operatorRevisions)} asked · automatic rounds not counted`}
          </span>
        }
      />
      <Figure
        label={`house rules learned, ${String(throughput.days)} days`}
        value={oneShot.rulesLearned}
        hint={<span className="figure__note">across every brand book</span>}
      />
      <Figure
        label="waits for you per delivered content"
        value={perDelivered(oneShot.waitsPerDelivered)}
        hint={
          <span className="figure__note">
            {oneShot.waitsPerDelivered === null
              ? none
              : `${String(oneShot.waits)} stops · planning questions and gates`}
          </span>
        }
      />
      {bible !== null && <GenericLinesFigure bible={bible} none={none} />}
    </div>
  );
}

/**
 * Does the bible work? (the ad bible US-033) The generic lines each delivered
 * content's script still holds — banned words on agents' lines, padded
 * repairs, unbacked claims, a stray descriptor, a repeated hook — with the
 * contents that hold the most named under it.
 */
function GenericLinesFigure({ bible, none }: { readonly bible: BibleFigures; readonly none: string }) {
  const counted = bible.contents.filter(
    (content): content is { id: string; name: string; genericLines: number } => content.genericLines !== null,
  );
  const worst = [...counted]
    .filter((content) => content.genericLines > 0)
    .sort((a, b) => b.genericLines - a.genericLines)
    .slice(0, 3);
  return (
    <Figure
      label="generic lines per delivered content"
      value={bible.perDelivered === null ? '—' : bible.perDelivered.toFixed(2)}
      hint={
        <span
          className="figure__note"
          title={counted.map((content) => `${content.name}: ${String(content.genericLines)}`).join('\n')}
        >
          {bible.perDelivered === null
            ? bible.contents.length === 0
              ? none
              : 'no delivered plan to read'
            : worst.length === 0
              ? `none in ${String(bible.measured)} delivered`
              : worst.map((content) => `${content.name} ${String(content.genericLines)}`).join(' · ')}
        </span>
      }
      tone={bible.perDelivered !== null && bible.perDelivered > 0 ? 'danger' : undefined}
    />
  );
}

/**
 * Dollars, to cents — except for a figure below a cent, which is a real
 * amount that `$0.00` would report as free. A per-day average over a
 * fortnight is routinely that small.
 */
function usd(value: number): string {
  if (value === 0) return '$0.00';
  if (Math.abs(value) < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/**
 * The storage row's three states. A bucket nobody has tested is not set up:
 * every check the connection test runs — write, signed read, CORS, delete —
 * is one an operator would otherwise meet for the first time when a finished
 * video failed to deliver.
 */
interface StorageStep {
  readonly done: boolean;
  readonly body: string;
  readonly action: string;
}

function storageStep(storage: StorageSettings | null): StorageStep {
  if (storage === null || !storage.configured) {
    return {
      done: false,
      body: 'Everything a content produces is delivered to an S3-compatible bucket. Hetzner Object Storage, AWS S3, Cloudflare R2, Backblaze B2 and MinIO all work through the same panel.',
      action: 'Add a bucket',
    };
  }
  if (storage.lastTest === null) {
    return {
      done: false,
      body: `${storage.bucket} is saved but has never been tested. The connection test writes, reads back through a signed URL, checks CORS and cleans up after itself.`,
      action: 'Test connection',
    };
  }
  if (!storage.lastTest.ok) {
    return {
      done: false,
      body: `${storage.bucket} failed its last connection test: ${storage.lastTest.message}`,
      action: 'Fix storage',
    };
  }
  return {
    done: true,
    body: `${storage.bucket} passed its connection test ${since(storage.lastTest.at)}.`,
    action: 'Open settings',
  };
}

/**
 * The four things an install needs before it can produce anything, in the
 * order they have to happen. Shown only while one of them is outstanding, so
 * the home page stops being a tutorial the moment the first content exists.
 *
 * Storage is last of the four but first in consequence: nothing can be
 * delivered before there is somewhere to put it, and the row is done only when
 * the bucket is configured *and* its last connection test passed — a saved
 * bucket nobody has tested is a bucket that has not been shown to work.
 */
function SetupChecklist({
  claude,
  repository,
  content,
  storage,
}: {
  readonly claude: boolean;
  readonly repository: boolean;
  readonly content: boolean;
  readonly storage: StorageStep;
}) {
  const steps = [
    {
      done: claude,
      title: 'Sign Claude Code in',
      body: 'A one-time browser login in a throwaway container; every content container then shares those credentials.',
      href: '/settings#claude',
      action: 'Open settings',
    },
    {
      done: repository,
      title: 'Add a repository with a deploy key',
      body: 'marketingchief generates the key; paste the public half into your forge. Read-only is enough — every clone is shallow and nothing is ever pushed.',
      href: '/repositories',
      action: 'Add repository',
    },
    {
      done: content,
      title: 'Create your first content',
      body: 'Pick the repositories, the formats and a tone, write the brief; planning happens with Claude in the browser.',
      href: '/content/new',
      action: 'New content',
    },
    {
      done: storage.done,
      title: 'Point it at a bucket',
      body: storage.body,
      href: '/settings#storage',
      action: storage.action,
    },
  ];
  const remaining = steps.filter((step) => !step.done).length;
  return (
    <Panel
      title="Get set up"
      icon="tasklist"
      meta={<Badge tone="wait">{remaining} to go</Badge>}
    >
      <ol className="steps">
        {steps.map((step, index) => (
          <li className={`step${step.done ? ' step--done' : ''}`} key={step.title}>
            <span className="step__marker">{step.done ? <Icon name="check" /> : index + 1}</span>
            <div className="step__main">
              <span className="step__title">{step.title}</span>
              <span className="step__body">{step.body}</span>
            </div>
            {!step.done && (
              <Link className="button button--small" href={step.href}>
                {step.action}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  );
}
